import { useTranslation } from 'react-i18next'
import { useLocation, useSearchParams } from 'react-router-dom'
import { Form, useFormikContext } from 'formik'
import { Grid, useMediaQuery, useTheme } from '@mui/material'

import {
  FormTitle,
  TextInput,
  GenerateAIButton,
  FormSubmitButton,
  UnSdgFormInput,
  ImpactTypeInput,
  ImpactPeriodInput,
  FormImage,
  HelperWrapper,
} from 'src/components'
import { MobileNewButtons } from './mobile-new-buttons.component'
import { MobileEditButtons } from './mobile-edit-buttons.component'

import { ETranslationNs, IImpactLibraryForm } from 'src/types'

import useStyles from './styles'
import { useMenuItems, usePrompt } from 'src/hooks'
import { createRelativePath } from 'src/common/utils'

interface IImpactLibraryFormProps {
  isAddingNew?: boolean
  isLoading: boolean
}

const ImpactLibraryForm = ({ isAddingNew, isLoading }: IImpactLibraryFormProps) => {
  const [t] = useTranslation([ETranslationNs.Common, ETranslationNs.Navbar])
  const classes = useStyles()
  const { values, handleChange, errors, setFieldValue, isSubmitting, dirty } =
    useFormikContext<IImpactLibraryForm>()
  const theme = useTheme()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const libraryDuplicate = searchParams.get('libraryDuplicate')
  usePrompt({
    message: isLoading ? t('prompt-loading-changes.v') : t('prompt-unsaved-changes.v'),
    when: (dirty && !isSubmitting) || isLoading,
    beforeUnload: true,
  })

  const isTablet = useMediaQuery(theme.breakpoints.down('tablet'))

  const { handleChooseMenuItem, menuItems } = useMenuItems([
    { title: 'profile-header-title.v', isActive: true },
  ])

  const handleDescriptionGenerate = (value: string) => setFieldValue('description', value)

  const breadcrumbs = [
    {
      text: t('impacts-library-header-title.v', { ns: ETranslationNs.Navbar }),
      path: createRelativePath(location),
    },
    { text: isAddingNew ? 'impacts-header-title-add.v' : values.name },
  ]

  return (
    <Form>
      {!isTablet && (
        <FormTitle
          menuItems={menuItems}
          handleChooseMenuItem={handleChooseMenuItem}
          breadcrumbs={breadcrumbs}
          setFieldValue={setFieldValue}
          title={values.name}
          imageComponent={<FormImage alt={values.name} />}
        />
      )}
      <Grid className={classes.form}>
        <Grid className={classes.formWrapper} container>
          {isTablet && (
            <Grid className={classes.mainImageWrapper}>
              <FormImage alt={values.name} />
            </Grid>
          )}

          <TextInput
            inputName="name"
            handleChange={handleChange}
            value={values.name}
            error={errors.name ? t(errors.name + '.v') : ''}
            inputUpperLabel={t('impacts-form-name-input-label.v') + '*'}
            inputPlaceholder={t('impacts-form-name-input-placeholder.v')}
          />
          <ImpactPeriodInput />
          <ImpactTypeInput />
          <UnSdgFormInput />
          <TextInput
            inputName="url"
            handleChange={handleChange}
            value={values.url}
            error={errors.url}
            inputUpperLabel={t('url-input-label.v')}
            inputPlaceholder={t('impact-library-url-placeholder.v')}
          />
          <Grid className={classes.inputWrapper}>
            <TextInput
              inputName="description"
              handleChange={handleChange}
              value={values.description}
              error={errors.description ? t(errors.description + '.v') : ''}
              inputPlaceholder={t('description-input-placeholder.v')}
              inputUpperLabel={t('description-input-label.v') + '*'}
              multiline={true}
            />
            <GenerateAIButton
              generationType="Impact"
              setGeneratedValue={handleDescriptionGenerate}
            />
          </Grid>
          {isTablet && (isAddingNew ? <MobileNewButtons /> : <MobileEditButtons />)}
        </Grid>
        {!isTablet && isAddingNew && (
          <FormSubmitButton
            notDisabledIfDirty={!!libraryDuplicate}
            withoutIcon
            validText={<HelperWrapper t="save-button-text" />}
            invalidText={<HelperWrapper t="save-button-text" />}
          />
        )}
        {!isAddingNew && (
          <FormSubmitButton
            validText={<HelperWrapper t="save-changes-button-text" ns={ETranslationNs.Form} />}
            invalidText={<HelperWrapper t="impacts-form-submit-button" />}
          />
        )}
      </Grid>
    </Form>
  )
}

export { ImpactLibraryForm }
